// Light and dark, in memory, for the length of this tab.
//
// NOT A CLIENT THEME. A client theme is a closed token set, contrast-checked
// and signed into the activation bundle — 15-branding-and-theming.md, and
// xp/ui/src/theme.ts's `checkTheme`. This file only chooses which of Nova's
// two default palettes is showing. A brand that arrives later sets the same
// custom properties this switch already reads; it does not grow a second
// stylesheet. The shipped system is Brand: pills on --xp-radius, boxes on
// --xp-radius-sm. Registration, the wheel and the kiosk board all apply it.
//
// NOTHING IS WRITTEN TO THE DEVICE. The console may remember a desk preference
// in localStorage because the operator asked for a tool they will reopen. This
// page is on a stranger's phone. Remembering light-or-dark there is a
// persistent identifier wearing a convenience's clothes, and it would put a
// key on PERMITTED_STORAGE the moment it was written — privacy/storage.ts is
// the list, and it is empty. The choice lives in React state and on
// `document.documentElement`. Walk away and it is gone, which is the same
// sentence the welcome screen already tells them about their details.
//
// The first paint follows `prefers-color-scheme` so a phone already in dark
// mode does not flash white. That read is not storage.

import { BRAND_XP_TOKENS, SHIPPED_SYSTEM } from "@nova/xp-visitor/themes";

export type PageTheme = "light" | "dark";

export function preferredTheme(
  query: { matches: boolean } | null | undefined = matchMediaSafe("(prefers-color-scheme: dark)"),
): PageTheme {
  return query?.matches === true ? "dark" : "light";
}

export function applyTheme(theme: PageTheme): void {
  const next = theme === "dark" ? "dark" : "light";
  document.documentElement.setAttribute("data-theme", next);
  document.documentElement.setAttribute("data-system", SHIPPED_SYSTEM);
  const root = document.documentElement;
  for (const [name, value] of Object.entries(BRAND_XP_TOKENS[next])) {
    if (typeof value === "string") root.style.setProperty(name, value);
  }
}

const CLIENT_TOKENS = [
  "--xp-bg",
  "--xp-surface",
  "--xp-surface-2",
  "--xp-line",
  "--xp-line-strong",
  "--xp-ink",
  "--xp-ink-2",
  "--xp-ink-3",
  "--xp-accent",
  "--xp-accent-ink",
  "--xp-data",
  "--xp-tint",
  "--xp-good",
  "--xp-good-soft",
  "--xp-warn",
  "--xp-warn-soft",
  "--xp-bad",
  "--xp-bad-soft",
  "--xp-radius",
  "--xp-radius-sm",
  "--xp-sans",
  "--xp-shadow",
  "--xp-scrim",
  "--xp-foot",
] as const;

/**
 * The visitor's light/dark choice, plus the client's token overrides for that
 * mode. A missing dark set falls back to the light set (and the other way
 * round) so a stand that only painted one page still has a markable palette.
 * Tokens the bundle did not name stay as Brand so a half-painted stand still has a page.
 */
export function applyClientTokens(
  mode: PageTheme,
  light: Readonly<Record<string, string>> | null,
  dark: Readonly<Record<string, string>> | null,
): void {
  applyTheme(mode);
  const chosen = mode === "dark" ? (dark ?? light) : (light ?? dark);
  const root = document.documentElement;
  for (const token of CLIENT_TOKENS) {
    const value = chosen?.[token];
    if (typeof value === "string" && value.trim() !== "") root.style.setProperty(token, value);
  }
  // The footer band is Brand's own shade. A client palette that names no
  // footer gets its own second surface (web.css's fallback), not Brand's grey
  // under the client's colours.
  if (chosen && typeof chosen["--xp-foot"] !== "string") root.style.removeProperty("--xp-foot");
}

function matchMediaSafe(query: string): { matches: boolean } | null {
  try {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null;
    return window.matchMedia(query);
  } catch {
    return null;
  }
}
